'use client'

import * as React from 'react'
import { Search, Download, TrendingUp, TrendingDown, Minus, Target } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'
import { cn } from 'lib/utils'
import { ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip } from 'recharts'

function parseAmount(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function getRate(ind) {
  const target = parseAmount(ind.target ?? ind.targetValue ?? ind.targets?.[0]?.value ?? 0)
  const current = parseAmount(ind.currentValue ?? ind.current ?? ind.actual ?? ind.value ?? 0)
  return target > 0 ? Math.round((current / target) * 100) : 0
}

function getStatus(rate) {
  if (rate >= 80) return { label: 'On Track', color: 'text-success', bg: 'bg-success/15 text-success', icon: <TrendingUp className="size-3" /> }
  if (rate >= 50) return { label: 'At Risk', color: 'text-warning', bg: 'bg-warning/15 text-warning', icon: <Minus className="size-3" /> }
  return { label: 'Off Track', color: 'text-destructive', bg: 'bg-destructive/15 text-destructive', icon: <TrendingDown className="size-3" /> }
}

export default function IndicatorsPage() {
  const [search, setSearch] = React.useState('')
  const [levelFilter, setLevelFilter] = React.useState('all')
  const [projectFilter, setProjectFilter] = React.useState('all')
  const [indicators, setIndicators] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadData() {
      try {
        const [indicatorSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'indicators')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        setIndicators(indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setIndicators([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [])

  const filtered = indicators.filter((ind) => {
    const matchSearch = ind.name.toLowerCase().includes(search.toLowerCase())
    const matchLevel = levelFilter === 'all' || ind.level === levelFilter
    const projectId = ind.projectId
    const matchProject = projectFilter === 'all' || projectId === projectFilter
    return matchSearch && matchLevel && matchProject
  })

  const radarData = projects.filter(p => p.status !== 'on-hold').map((proj) => {
    const projIndicators = indicators.filter(i => i.projectId === proj.id)
    const avgRate = projIndicators.length > 0
      ? Math.round(projIndicators.reduce((s, i) => s + getRate(i), 0) / projIndicators.length)
      : 0
    return { project: proj.code || proj.shortCode || proj.title || proj.id, achievement: avgRate }
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Indicators Tracker</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance tracking across all project indicators with target vs. actual analysis</p>
        </div>
        <Button variant="outline" className="gap-2 text-foreground"><Download className="size-4" />Export CSV</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : indicators.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Indicators</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : indicators.filter(i => getRate(i) >= 80).length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">On Track</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{loading ? '—' : indicators.filter(i => { const r = getRate(i); return r >= 50 && r < 80 }).length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">At Risk</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{loading ? '—' : indicators.filter(i => getRate(i) < 50).length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Off Track</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Radar Chart */}
        <Card className="border border-border lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Achievement by Project</CardTitle>
            <CardDescription className="text-xs">Average indicator achievement rate per project</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading indicators...</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="oklch(0.9 0.01 145)" />
                  <PolarAngleAxis dataKey="project" tick={{ fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
                  <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                  <Radar name="Achievement %" dataKey="achievement" stroke="oklch(0.35 0.12 145)" fill="oklch(0.35 0.12 145)" fillOpacity={0.3} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Indicator Table */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search indicators..." className="pl-10 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[120px] h-9"><SelectValue placeholder="Level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="goal">Goal</SelectItem>
                <SelectItem value="outcome">Outcome</SelectItem>
                <SelectItem value="output">Output</SelectItem>
                <SelectItem value="activity">Activity</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.code || p.shortCode || p.title || p.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Indicator</TableHead>
                  <TableHead className="text-xs">Level</TableHead>
                  <TableHead className="text-xs text-right">Baseline</TableHead>
                  <TableHead className="text-xs text-right">Target</TableHead>
                  <TableHead className="text-xs text-right">Actual</TableHead>
                  <TableHead className="text-xs">Progress</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                      Loading indicators...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                      No indicators found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((ind) => {
                    const rate = getRate(ind)
                    const status = getStatus(rate)
                    const baseline = ind.baseline ?? ind.baselineValue ?? '—'
                    const target = ind.target ?? ind.targetValue ?? ind.targets?.[0]?.value ?? '—'
                    const actual = ind.currentValue ?? ind.current ?? ind.actual ?? ind.value ?? '—'
                    return (
                      <TableRow key={ind.id}>
                        <TableCell className="text-xs text-foreground max-w-[220px]">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{ind.name}</span>
                            <span className="text-[10px] text-muted-foreground">{ind.frequency} | {ind.unit}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] capitalize">{ind.level}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right text-muted-foreground">{baseline}</TableCell>
                        <TableCell className="text-xs text-right font-medium text-foreground">{target}</TableCell>
                        <TableCell className="text-xs text-right font-bold text-foreground">{actual}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <Progress value={Math.min(rate, 100)} className="h-1.5 flex-1" />
                            <span className={cn('text-[10px] font-semibold', status.color)}>{rate}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('text-[10px] gap-1', status.bg)}>{status.icon}{status.label}</Badge>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  )
}
